import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Paper, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Grid, 
  CircularProgress,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import ProjectSelector from '../shared/ProjectSelector';

interface BankWithdrawModalProps {
  open: boolean;
  onClose: () => void;
  bankAccountId: string;
  bankAccountName: string;
  bankName: string;
  currency: string;
  companyId: string;
  currencySymbol: string;
  balance: number;
  onSuccess?: () => void;
  subAccountInfo?: {
    subAccountId: string;
    subAccountName: string;
  };
}

const BankWithdrawModal: React.FC<BankWithdrawModalProps> = ({
  open,
  onClose,
  bankAccountId,
  bankAccountName,
  bankName,
  currency,
  companyId,
  currencySymbol,
  balance,
  onSuccess,
  subAccountInfo
}) => {
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [documentNo, setDocumentNo] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // PYS: hareketi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);
  // ✅ Geçmiş tarihli işlem: toggle açıldığında tarih seçici görünür.
  // İşaretli ve tarih girilmişse hareket kaydı o tarihe yazılır (banka transferi ile aynı desen).
  const [isBackdated, setIsBackdated] = useState(false);
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  // Modal kapanınca formu sıfırla
  useEffect(() => {
    if (!open) {
      setAmount('');
      setDescription('');
      setPaymentMethod('cash');
      setDocumentNo('');
      setError(null);
      setProjectId(null);
      // ✅ Geçmiş tarihli işlem alanını sıfırla (varsayılan: kapalı / bugün)
      setIsBackdated(false);
      const today = new Date();
      setTransactionDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
    }
  }, [open]);

  // Binlik ayraç ile sayı formatlama (ondalık destekli)
  const formatNumberWithThousandsSeparator = (value: string): string => {
    // Sadece rakamları ve virgülü tut
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    // Birden fazla virgül varsa sadece ilkini tut
    const commaCount = (cleanValue.match(/,/g) || []).length;
    if (commaCount > 1) {
      const firstCommaIndex = cleanValue.indexOf(',');
      const beforeComma = cleanValue.substring(0, firstCommaIndex);
      const afterComma = cleanValue.substring(firstCommaIndex + 1).replace(/,/g, '');
      return formatNumberWithThousandsSeparator(beforeComma + ',' + afterComma);
    }
    
    // Virgülden önceki ve sonraki kısımları ayır
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? parts[1] : null; // null = virgül yok, '' = virgül var ama rakam yok
    
    // Ondalık kısmı maksimum 2 basamak ile sınırla
    if (decimalPart && decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    // Binlik ayraçlarını ekle (integer kısmına)
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Decimal varsa ekle (virgül girilmişse, rakam girilmese bile virgülü göster)
    return decimalPart !== null ? `${formattedInteger},${decimalPart}` : formattedInteger;
  };

  // Formatlanmış sayıdan ham sayı al ("1.000,50" → 1000.50)
  const parseFormattedNumber = (value: string): number => {
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Sayısal girdi kontrolü
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatNumberWithThousandsSeparator(value);
    setAmount(formatted);
  };

  // Para çekme işlemi (SQL API)
  const handleWithdraw = async () => {
    // Validasyon
    const withdrawAmount = parseFormattedNumber(amount);
    if (!amount || withdrawAmount <= 0) {
      setError('Lütfen geçerli bir miktar giriniz.');
      return;
    }

    // Bakiye kontrolü
    if (withdrawAmount > balance) {
      setError('Çekilmek istenen miktar hesap bakiyesinden büyük olamaz.');
      return;
    }

    // ✅ Geçmiş tarihli işlem: işaretliyse seçilen tarihi hesapla.
    let backdatedIso: string | null = null;
    if (isBackdated) {
      const parts = transactionDate.split('-');
      const selected = parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0)
        : new Date(NaN);
      if (isNaN(selected.getTime())) {
        setError('Geçerli bir işlem tarihi seçiniz.');
        return;
      }
      // Gün kayması yaşanmaması için öğlen (12:00) sabitlenir.
      backdatedIso = selected.toISOString();
    }

    setSaving(true);
    setError(null);

    try {
      console.log('💸 Bankadan para çekme işlemi başlatılıyor (SQL)...');
      
      // Hesap ID'yi sayıya çevir
      const numericBankAccountId = parseInt(bankAccountId, 10);
      if (isNaN(numericBankAccountId)) {
        throw new Error('Geçersiz banka hesabı ID formatı');
      }
      
      // Alt hesap ID'si varsa onu da çevir
      let numericSubAccountId: number | null = null;
      if (subAccountInfo) {
        numericSubAccountId = parseInt(subAccountInfo.subAccountId, 10);
        if (isNaN(numericSubAccountId)) {
          throw new Error('Geçersiz alt hesap ID formatı');
        }
      }

      // Miktarı sayıya çevir (zaten yukarıda hesaplandı)
      
      // SQL API için request body
      const requestBody: Record<string, any> = {
        bankSubAccountId: numericSubAccountId,
        amount: withdrawAmount,
        description: description.trim() || `Bankadan para çekme: ${bankName} - ${subAccountInfo ? subAccountInfo.subAccountName : bankAccountName}`,
        createdBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      // Yalnızca geçmiş tarihli işlem seçildiyse tarihi gönder; aksi halde backend bugünü kullanır.
      if (backdatedIso) {
        requestBody.transactionDate = backdatedIso;
      }
      if (projectId) {
        requestBody.projectId = projectId;
      }

      console.log('📤 Gönderilen veri:', requestBody);
      
      // SQL API'ye withdraw isteği gönder
      const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_WITHDRAW(numericBankAccountId), requestBody);
      const result = response.data;
      console.log('✅ Para çekme işlemi başarılı (SQL):', result);
      
      // Başarıyla tamamlandı
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error('❌ Para çekme işlemi sırasında hata oluştu:', error);
      setError(error.response?.data?.message || (error instanceof Error ? error.message : 'Para çekme işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={!saving ? onClose : undefined}
      aria-labelledby="banka-para-cekme-modal"
    >
      <Paper 
        sx={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 480 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" fontWeight="bold" color="primary">
            Bankadan Para Çekme
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{bankName}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom>
            Hesap: <strong>{subAccountInfo ? subAccountInfo.subAccountName : bankAccountName}</strong>
            {subAccountInfo && (
              <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 1 }}>
                (Alt Hesap)
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Mevcut Bakiye: <strong>{balance.toLocaleString('tr-TR')} {currencySymbol}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            Bu işlemle hesaptan <strong>{currency}</strong> para biriminde para çekebilirsiniz.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              required
              fullWidth
              label="Çekilecek Miktar"
              value={amount}
              onChange={handleAmountChange}
              type="text"
              inputProps={{
                style: { textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{currencySymbol}</InputAdornment>,
              }}
              error={!!error && !amount}
              helperText={!amount && !!error ? 'Bu alan zorunludur' : ''}
              placeholder="Örn: 1.000,50"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="payment-method-label">Ödeme Yöntemi</InputLabel>
              <Select
                labelId="payment-method-label"
                value={paymentMethod}
                label="Ödeme Yöntemi"
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <MenuItem value="cash">Nakit</MenuItem>
                <MenuItem value="bank_transfer">Havale/EFT</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Belge/Makbuz No"
              value={documentNo}
              onChange={(e) => setDocumentNo(e.target.value)}
              placeholder="Varsa belge numarası"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Açıklama"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              placeholder="İsteğe bağlı açıklama ekleyebilirsiniz"
            />
          </Grid>

          <Grid item xs={12}>
            <ProjectSelector value={projectId} onChange={setProjectId} disabled={saving} />
          </Grid>

          {/* Geçmiş Tarihli İşlem */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isBackdated}
                  onChange={(e) => setIsBackdated(e.target.checked)}
                  disabled={saving}
                  color="primary"
                />
              }
              label="Geçmiş tarihli işlem yap"
            />

            {isBackdated && (
              <>
                <TextField
                  label="İşlem Tarihi *"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  fullWidth
                  required
                  disabled={saving}
                  sx={{ mt: 1, mb: 1 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`
                  }}
                />
                <Alert severity="warning">
                  Bu işlem seçtiğiniz tarihe kaydedilecektir. Hareket kaydı o tarihe ait olacaktır.
                </Alert>
              </>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
            disabled={saving}
          >
            İptal
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleWithdraw}
            disabled={!amount || saving || parseFormattedNumber(amount) <= 0 || parseFormattedNumber(amount) > balance || (isBackdated && !transactionDate)}
            startIcon={saving ? <CircularProgress size={24} /> : null}
          >
            {saving ? 'İşleniyor...' : 'Para Çek'}
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default BankWithdrawModal; 